import  { motion } from 'framer-motion';
import { customPieces } from '../data';
import AnimatedDesignProcess from '../components/AnimatedDesignProcess';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

const CustomJewelryPage = () => {
  const [ref, isInView] = useIntersectionObserver<HTMLDivElement>({ threshold: 0.1, once: true });

  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl mb-2">Custom Jewelry</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Create a one-of-a-kind piece that tells your unique story. Our expert artisans 
            will work closely with you to bring your vision to life.
          </p>
        </motion.div>
        
        <AnimatedDesignProcess />
        
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="my-16"
        >
          <h2 className="font-serif text-3xl text-center mb-12">Past Custom Creations</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {customPieces.map((piece, index) => (
              <motion.div 
                key={piece.id} 
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.3 + index * 0.2,
                  ease: "easeOut" 
                }}
              >
                <div className="mb-4 overflow-hidden">
                  <motion.img 
                    src={piece.image} 
                    alt={piece.name} 
                    className="w-full h-80 object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <h3 className="font-serif text-xl mb-2">{piece.name}</h3>
                <p className="text-gray-600">{piece.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        <motion.div 
          className="bg-beige-light p-8 md:p-12 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="font-serif text-3xl text-center mb-6">Begin Your Custom Journey</h2>
          <motion.form 
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="name"
                  className="w-full p-3 border border-gray-300 focus:border-gold focus:ring-0 outline-none"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  className="w-full p-3 border border-gray-300 focus:border-gold focus:ring-0 outline-none"
                  placeholder="Your email"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="jewelry-type" className="block text-sm font-medium text-gray-700 mb-1">Jewelry Type</label>
              <select 
                id="jewelry-type" 
                className="w-full p-3 border border-gray-300 focus:border-gold focus:ring-0 outline-none"
              >
                <option value="">Select type</option>
                <option value="ring">Ring</option>
                <option value="necklace">Necklace</option>
                <option value="bracelet">Bracelet</option>
                <option value="earrings">Earrings</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">Budget Range</label>
              <select 
                id="budget" 
                className="w-full p-3 border border-gray-300 focus:border-gold focus:ring-0 outline-none"
              >
                <option value="">Select budget range</option>
                <option value="1000-3000">$1,000 - $3,000</option>
                <option value="3000-5000">$3,000 - $5,000</option>
                <option value="5000-10000">$5,000 - $10,000</option>
                <option value="10000+">$10,000+</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Your Vision</label>
              <textarea
                id="message"
                rows={5}
                className="w-full p-3 border border-gray-300 focus:border-gold focus:ring-0 outline-none"
                placeholder="Describe your vision or any specific details about your custom piece"
              />
            </div>
            
            <div className="flex justify-center">
              <motion.button
                type="submit"
                className="bg-gold hover:bg-gold-dark text-white px-8 py-3 uppercase tracking-wide text-sm transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Submit Inquiry
              </motion.button>
            </div>
          </motion.form>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomJewelryPage;
 